
// Time constants in seconds
const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const WEEK = DAY * 7;
const YEAR = WEEK * 52;

/** @description Converts a date to a short date string (dd/MM/yyyy).  
 * @param {string} inputFormat Date in string format.  
 * @return {string}
 */
export function convertToShortDateString(inputFormat?: string): string | undefined {
    if (!inputFormat) {
        return undefined;
    }

    if (isIso8601DateString(inputFormat)) {
        // Extract date portion directly to avoid timezone conversion issues
        const datePart = inputFormat.split('T')[0];
        const [year, month, day] = datePart.split('-');
        return `${day}/${month}/${year}`;
    }
    
    if (isShortDateFR(inputFormat)) {
        return inputFormat;
    }

    return undefined;
}

/** @description Converts a date to an approximate date string (X time ago).  
 * @param {string} inputFormat Date in string format.  
 * @return {string}
 */
export function convertToApproximateDateString(inputFormat: string): string | undefined {
    if (!isIso8601DateString(inputFormat)) {
        return undefined;
    }

    const givenDate = new Date(inputFormat);
    const currentDate = new Date();
    let difference = (currentDate.getTime() - givenDate.getTime()) / 1000;

    const suffix = difference < 0 ? "until" : "ago";
    difference = Math.abs(difference);

    if (difference < MINUTE) {
        const seconds = Math.floor(difference);
        return `${seconds} second${seconds === 1 ? '' : 's'} ${suffix}`;
    }
    
    if (difference < HOUR) {
        const minutes = Math.floor(difference / MINUTE);
        return `${minutes} minute${minutes === 1 ? '' : 's'} ${suffix}`;
    }
    
    if (difference < DAY) {
        const hours = Math.floor(difference / HOUR);
        return `${hours} hour${hours === 1 ? '' : 's'} ${suffix}`;
    }
    
    if (difference < WEEK) {
        const days = Math.floor(difference / DAY);
        return `${days} day${days === 1 ? '' : 's'} ${suffix}`;
    }
    
    if (difference < YEAR) {
        const weeks = Math.floor(difference / WEEK);
        return `${weeks} week${weeks === 1 ? '' : 's'} ${suffix}`;
    }
    
    const years = Math.floor(difference / YEAR);
    return `${years} year${years === 1 ? '' : 's'} ${suffix}`;
}

/** @description Checks if a string is in ISO8601 format (yyyy-MM-ddTHH:mm:ss with Z or timezone offset).  
 * @param {string} inputFormat Date in string format.  
 * @return {boolean}
 */
export function isIso8601DateString(inputFormat: string): boolean {
    // Matches ISO 8601 with Z or timezone offset like +11:00 or -05:00
    return /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.\d*)?)(Z|[+-]\d{2}:\d{2})$/.test(inputFormat);
}

export function isShortDateFR(inputFormat: string): boolean {
    return /^$|(0?[1-9]|[12][0-9]|3[01])[- \/.](0?[1-9]|1[012])[- \/.](19|20)\d\d$/.test(inputFormat);
}

// Deprecated: use isIso8601DateString instead
export const isUtcDateString = isIso8601DateString;